import { useMemo, useRef, useState } from "react";
import { Card, Modal, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TopicStatistics } from "../types";
import { STYLE } from "../../../config/constraits";
import { addFilter, saveBeforeFilter } from "../../../redux/filtersSlice";
import ChartShareActions from "../../../components/ChartShareActions";
import { RootState } from "../../../redux/store";
import { generateCsvFile } from "../../../utils/downloadCsvFile";
import zoomIcon from "@/assets/arrow-up-right-and-arrow-down-left-from-center-light-full.svg";
import unZoomIcon from "@/assets/arrow-down-left-and-arrow-up-right-to-center-light-full.svg";
import TopicsClaimTableContent from "../../claims/components/dashboard/topics/TopicsClaimTableContent";
import { AuditActionEnum } from "../../auditLogs/types";
import { SelectedFilter } from "../../../components/Filters/types";
import { useCurrentClient } from "../../auth/api/getCurrentClient";
import { getSentimentValues } from "../../../utils/sentimentUtils";

interface TopicsTableProps {
    topics: TopicStatistics[];
}

export default function TopicsClaimTable({ topics }: TopicsTableProps) {
    const { t } = useTranslation(["dashboard", "claim"]);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { data: currentClient } = useCurrentClient();

    const feelingchartRef = useRef<HTMLDivElement>(null);
    const selectedFilters = useSelector((state: RootState) => state.filters.claims.selectedFilters);
    const [showTopicsModal, setShowTopicsModal] = useState(false);

    const previewTopics = useMemo(() => topics.slice(0, 5), [topics]);

    const handleTopicClick = (topicId: number, sentiment?: 'positive' | 'neutral' | 'negative' | 'alert') => {
		const topicFilters: SelectedFilter = {
            name: 'Topic',
            values: [topicId.toString()],
            description: t("dashboard:filters.Topics")
        };

		if (sentiment) {
			const values: string[] = getSentimentValues(sentiment, currentClient);
			const fieldName: string = sentiment != 'alert' ? "SentimentAI" : "AlertAI";
            
            topicFilters.relatedFilters = [{
				name: fieldName,
				values,
				description: `${topics.find(t => t.topicId == topicId)?.topicName} (${t(`dashboard:filters.${fieldName}`)})`
			}]
		}

        dispatch(saveBeforeFilter("claims"));
        dispatch(addFilter({ table: 'claims', filter: topicFilters }));
        
        navigate("/claims/historic");
    };

    const exportTopicsClaimsCsvData = async (): Promise<void> => {
        await generateCsvFile({
            headers: [
                t("claim:trendingTopics.export.headerTopic"),
                t("claim:trendingTopics.export.headerClaimsCount"),
                t("claim:trendingTopics.export.headerConversationPercentage"),
                t("claim:trendingTopics.export.headerPositive"),
                t("claim:trendingTopics.export.headerNeutral"),
                t("claim:trendingTopics.export.headerNegative"),
                t("claim:trendingTopics.export.headerAlerts"),
            ],
            rows: topics,
            mapRow: (item) => {
                const positive = item.positiveComments ?? 0;
                const neutral = item.neutralComments ?? 0;
                const negative = item.negativeComments ?? 0;
                const claimsCount = positive + neutral + negative;
                const percentage = Number((item.percentage ?? 0).toFixed(2));

                return [
                    item.topicName ?? "",
                    claimsCount,
                    percentage,
                    positive,
                    neutral,
                    negative,
                    item.urgency ?? 0,
                ];
            },
            fileBaseName: "claims_trending_topics",
            trailingSemicolon: true,
            audit: {
                entity: "DashboardIndicator",
                action: AuditActionEnum.ExportCsv,
                source: window.location.pathname,
                description: "CSV export of claim trending topics",
                filters: selectedFilters
            },
        });
    };

    return (
        <div ref={feelingchartRef}>
            <Modal
                show={showTopicsModal}
                onHide={() => setShowTopicsModal(false)}
                size="xl"
                centered
            >
                <Modal.Header>
                    <Modal.Title>{t("claim:trendingTopics.labelTitle")}</Modal.Title>
                    <Button
                        type="button"
                        variant="link"
                        className="ms-auto p-0 border-0 bg-transparent topics-claim-toggle-icon"
                        onClick={() => setShowTopicsModal(false)}
                        aria-label={t("common:buttons.buttonClose")}
                        title={t("common:buttons.buttonClose")}
                    >
                        <img
                            src={unZoomIcon}
                            alt={t("common:buttons.buttonClose")}
                            width={22}
                            height={22}
                        />
                    </Button>
                </Modal.Header>
                <Modal.Body style={{ backgroundColor: "#F5F5F5" }}>
                    <TopicsClaimTableContent topics={topics} onTopicClick={handleTopicClick} centered />
                </Modal.Body>
            </Modal>

            <Card
                style={{
                    backgroundColor: "#F5F5F5",
                    border: "none",
                    borderRadius: STYLE.BORDER_RADIUS,
                }}
                className="p-4 d-flex justify-content-center text-center"
            >
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 style={{ textAlign: "left" }}>{t("claim:trendingTopics.labelTitle")}</h4>
                    <div className="d-flex align-items-center gap-2">
                        <img
                            className="topics-claim-toggle-icon"
                            title={t("common:buttons.buttonSurveys")}
                            src={zoomIcon}
                            alt={t("common:buttons.buttonSurveys")}
                            width={15}
                            height={15}
                            onClick={() => setShowTopicsModal(true)}
                        />
                        <ChartShareActions
                            chartRef={feelingchartRef}
                            title={t("claim:trendingTopics.labelTitle")}
                            filters={selectedFilters}
                            t={t}
                            exportCsvAction={exportTopicsClaimsCsvData}
                        />
                    </div>
                </div>
                <TopicsClaimTableContent topics={previewTopics} onTopicClick={handleTopicClick} />
            </Card>
        </div>
    );
}
